import React from "react";
import { Link } from "react-router-dom";
import Logo from "../brand/Logo";
import Badge from "../foundation/Badge";
import Container from "../foundation/Container";
import PartnerLink from "../marketing/PartnerLink";

const columns = [
  {
    heading: "Products",
    links: [
      { label: "Business Funding", to: "/businesses" },
      { label: "ZenScore", to: "/zenscore" },
      { label: "Funding Marketplace", to: "/marketplace", badge: "Soon" },
    ],
  },
  {
    heading: "Solutions",
    links: [
      { label: "For Businesses", to: "/businesses" },
      { label: "For Funding Partners", to: "/partners" },
    ],
  },
  {
    heading: "Technology",
    links: [
      { label: "ZenScore", to: "/zenscore" },
      { label: "Mastercard Finicity", partner: "finicity" },
      { label: "Uptiq AI", partner: "uptiq" },
    ],
  },
  {
    heading: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Careers", to: "/about" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    heading: "Resources",
    links: [
      { label: "Blog", to: "/resources" },
      { label: "Help Center", to: "/resources" },
      { label: "Security", to: "/resources" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
];

const SocialIcon = ({ name, children, href = "#", testId }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    aria-label={name}
    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] text-text-secondary transition-colors hover:border-brand-emerald/50 hover:text-brand-emerald"
    data-testid={testId}
  >
    {children}
  </a>
);

export const Footer = () => {
  return (
    <footer
      className="relative mt-24 border-t border-white/[0.06] bg-bg-base pt-20"
      data-testid="footer"
    >
      <Container>
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-3 lg:grid-cols-6">
          {columns.map((col) => (
            <div key={col.heading} className="flex flex-col gap-4">
              <h4 className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-muted">
                {col.heading}
              </h4>
              <ul className="flex flex-col gap-3">
                {col.links.map((link) => (
                  <li key={`${col.heading}-${link.label}`}>
                    {link.partner ? (
                      <PartnerLink
                        partner={link.partner}
                        showGlyph={false}
                        underline="hover"
                        className="text-sm !text-text-secondary hover:!text-brand-emerald"
                      >
                        {link.label}
                      </PartnerLink>
                    ) : (
                      <Link
                        to={link.to}
                        className="group inline-flex items-center gap-2 text-sm text-text-secondary transition-colors hover:text-white"
                        data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span>{link.label}</span>
                        {link.badge && (
                          <Badge variant="emerald" className="!py-0 !px-1.5 !text-[9px]">
                            {link.badge}
                          </Badge>
                        )}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Emerald divider */}
        <div className="relative mt-20 h-px w-full overflow-hidden">
          <div className="absolute inset-0 bg-white/[0.06]" />
          <div className="absolute left-1/2 top-0 h-px w-1/3 -translate-x-1/2 bg-gradient-to-r from-transparent via-brand-emerald/60 to-transparent" />
        </div>

        <div className="flex flex-col-reverse items-start justify-between gap-6 py-8 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <Logo size="md" />
            <span className="text-xs text-text-muted">
              © 2026 ZenMe Capital. All rights reserved.
            </span>
          </div>

          <div className="flex items-center gap-2">
            <SocialIcon name="LinkedIn" testId="footer-social-linkedin">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M19 3A2 2 0 0 1 21 5V19A2 2 0 0 1 19 21H5A2 2 0 0 1 3 19V5A2 2 0 0 1 5 3H19M18.5 18.5V13.2A3.26 3.26 0 0 0 15.24 9.94C14.39 9.94 13.4 10.46 12.92 11.24V10.13H10.13V18.5H12.92V13.57C12.92 12.8 13.54 12.17 14.31 12.17A1.4 1.4 0 0 1 15.71 13.57V18.5H18.5M6.88 8.56A1.68 1.68 0 0 0 8.56 6.88C8.56 5.95 7.81 5.19 6.88 5.19A1.69 1.69 0 0 0 5.19 6.88C5.19 7.81 5.95 8.56 6.88 8.56M8.27 18.5V10.13H5.5V18.5H8.27Z" />
              </svg>
            </SocialIcon>
            <SocialIcon name="X (Twitter)" testId="footer-social-x">
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </SocialIcon>
            <SocialIcon name="YouTube" testId="footer-social-youtube">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M23.5 6.19a3 3 0 0 0-2.12-2.12C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.57A3 3 0 0 0 .5 6.19 31.3 31.3 0 0 0 0 12a31.3 31.3 0 0 0 .5 5.81 3 3 0 0 0 2.12 2.12C4.5 20.5 12 20.5 12 20.5s7.5 0 9.38-.57a3 3 0 0 0 2.12-2.12A31.3 31.3 0 0 0 24 12a31.3 31.3 0 0 0-.5-5.81zM9.75 15.5v-7l6.5 3.5-6.5 3.5z" />
              </svg>
            </SocialIcon>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
